import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Save,
  X,
  Edit2,
  Users,
  Briefcase,
  Calendar,
  Building2,
  BookOpen,
  Code2,
  Phone,
  Shield,
  MapPin,
  Plus,
} from "lucide-react";
import { formatDateForDisplay, formatDateForInput } from "@/lib/utils";
import { 
  validateAadhaarNumber, 
  validateMobileNumber, 
  formatMobileNumber, 
  formatMobileWithCountryCode 
} from "@/utils/validation";

// Helper functions for date formatting
const formatDateForInputField = (date: Date | string | undefined): string => {
  if (!date) return '';
  if (date instanceof Date) {
    return date.toISOString().split('T')[0];
  }
  return new Date(date).toISOString().split('T')[0];
};
// Type definitions - using the new nested personal structure
interface ProfileData {
  name: string;
  email: string;
  phone: string;
  location: string;
  role: string;
  department: string;
  designation: string;
  joiningDate: string;
  employeeId: string;
  reportingManager: string;
  companyName: string;
  uniqueId: string;
  
  middleName: string;
  gender: string;
  placeOfBirth: string;
  dateOfBirth: string;
  maritalStatus: string;
  nationality: string;
  bloodGroup: string;
  displayName: string;
  firstName: string;
  lastName: string;
  currentAddress1: string;
  currentAddress2: string;
  currentCity: string;
  currentCountry: string;
  currentState: string;
  currentPinCode: string;
  sameAsCurrentAddress: boolean;
  permanentAddress1: string;
  permanentAddress2: string;
  permanentCity: string;
  permanentCountry: string;
  permanentState: string;
  permanentPinCode: string;
  educationEntries: Array<{
    degree: string;
    collegeName: string;
    specialization: string;
    startDate: Date;
    endDate: Date;
  }>;
  familyMembers: Array<{
    name: string;
    dateOfBirth: string;
    occupation: string;
    mobile: string;
    aadhaar: string;
    relationship: string;
  }>;
  personal?: {
    personalInfo?: {
      displayName?: string;
      middleName?: string;
      gender?: string;
      dateOfBirth?: string;
      maritalStatus?: string;
      nationality?: string;
      bloodGroup?: string;
      placeOfBirth?: string;
      companyName?: string;
     
    };
    familyInfo?: {
      familyMembers?: Array<{
        name: string;
        dateOfBirth: string;
        occupation: string;
        mobile: string;
        aadhaar: string;
        relationship: string;
      }>;
    };
    addressInfo?: {
      currentAddress1?: string;
      currentAddress2?: string;
      currentCity?: string;
      currentCountry?: string;
      currentState?: string;
      currentPinCode?: string;
      sameAsCurrentAddress?: boolean;
      permanentAddress1?: string;
      permanentAddress2?: string;
      permanentCity?: string;
      permanentCountry?: string;
      permanentState?: string;
      permanentPinCode?: string;
    };
    educationEntries?: Array<{
      degree: string;
      collegeName: string;
      specialization: string;
      startDate: Date;
      endDate: Date;
    }>;
    experienceDetails?: {
      experienceType?: string;
      totalYears: number;
      totalMonths: number;
      previousEmployers?: Array<{
        companyName: string;
        department?: string;
        jobTitle: string;
        startDate: Date;
        endDate: Date;
      }>;
    };
  };
}

interface ExperienceDetails {
  experienceType: "fresher" | "experience";
  totalYears: number;
  totalMonths: number;
  previousEmployers: Array<{
    companyName: string;
    department?: string;
    jobTitle: string;
    startDate: Date;
    endDate: Date;
  }>;
}

interface SectionProps {
  profileData: ProfileData;
  setProfileData: (data: any) => void;
  isEditing: boolean;
}

interface PreviousEmployerProps {
  experienceType: "fresher" | "experience";
  setExperienceType: React.Dispatch<
    React.SetStateAction<"fresher" | "experience">
  >;
  experienceDetails: ExperienceDetails;
  setExperienceDetails: React.Dispatch<React.SetStateAction<ExperienceDetails>>;
  isEditing: boolean;
  setProfileData: (data: any) => void;
}

// Floating Label Input Component
const FloatingLabelInput = ({
  label,
  value,
  onChange,
  type = "text",
  required = false,
  disabled = false,
  className = "",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const hasValue = value && value.length > 0;

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <Input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder=""
          disabled={disabled}
          className={`h-10 text-sm pt-4 pb-1 px-3 bg-white border-gray-200 focus:border-blue-300 ${
            hasValue || isFocused ? "border-blue-300" : ""
          }`}
        />
        <Label
          className={`absolute left-3 transition-all duration-200 pointer-events-none ${
            hasValue
              ? "text-xs text-gray-500 -top-2 bg-white px-1"
              : isFocused
              ? "text-xs text-blue-600 -translate-y-2 top-1"
              : "text-sm text-gray-500 top-3"
          }`}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      </div>
    </div>
  );
};

// Floating Label Select Component
const FloatingLabelSelect = ({
  label,
  value,
  onChange,
  options = [],
  required = false,
  disabled = false,
  className = "",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  required?: boolean;
  disabled?: boolean;
  className?: string;
}) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const hasValue = value && value.length > 0;

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        <Select
          value={value || ""}
          onValueChange={onChange}
          onOpenChange={(open) => setIsFocused(open)}
          disabled={disabled}
        >
          <SelectTrigger
            className={`bg-white border-gray-200 focus:border-blue-300 h-10 text-sm pt-4 pb-1 px-3 ${
              hasValue || isFocused ? "border-blue-300" : ""
            }`}
          >
            <SelectValue placeholder="" />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Label
          className={`absolute left-3 transition-all duration-200 pointer-events-none ${
            hasValue
              ? "text-xs text-gray-500 -top-2 bg-white px-1"
              : isFocused
              ? "text-xs text-blue-600 -translate-y-2 top-1"
              : "text-sm text-gray-500 top-3"
          }`}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </Label>
      </div>
    </div>
  );
};

// Helper function to update nested values
const updateNestedValue = (obj: any, path: string, value: any) => {
  const keys = path.split('.');
  const result = { ...obj };
  let current = result;
  
  for (let i = 0; i < keys.length - 1; i++) {
    if (!current[keys[i]]) {
      current[keys[i]] = {};
    }
    current = current[keys[i]];
  }
  
  current[keys[keys.length - 1]] = value;
  return result;
};

// Personal Information Section
export const PersonalInfoSection = ({
  profileData,
  setProfileData,
}: SectionProps) => {
  const [localIsEditing, setLocalIsEditing] = React.useState(false);
  const [localData, setLocalData] = React.useState(profileData);

  React.useEffect(() => {
    setLocalData(profileData);
  }, [profileData]);

  const handleSave = () => {
    // Send only the personalInfo object as expected by the parent component
    const personalInfo = {
      displayName: localData.personal?.personalInfo?.displayName || '',
      middleName: localData.personal?.personalInfo?.middleName || '',
      gender: localData.personal?.personalInfo?.gender || '',
      dateOfBirth: localData.personal?.personalInfo?.dateOfBirth || '',
      maritalStatus: localData.personal?.personalInfo?.maritalStatus || '',
      nationality: localData.personal?.personalInfo?.nationality || '',
      bloodGroup: localData.personal?.personalInfo?.bloodGroup || '',
      placeOfBirth: localData.personal?.personalInfo?.placeOfBirth || '',
      companyName: localData.personal?.personalInfo?.companyName || '',
    };
    
    setProfileData(personalInfo as any);
    setLocalIsEditing(false);
    toast.success("Personal information saved successfully");
  };

  const handleCancel = () => {
    setLocalData(profileData);
    setLocalIsEditing(false);
    toast.info("Edit cancelled");
  };

  const handleEdit = () => {
    setLocalIsEditing(true);
    toast.info("Edit mode enabled");
  };

  return (
    <Card className="border-none shadow-sm bg-white">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base font-bold bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
              Personal Information
            </CardTitle>
          </div>
          <div className="flex items-center gap-1">
            {localIsEditing && (
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                className="gap-1 bg-blue-600 hover:bg-blue-700 text-xs px-2 py-1"
              >
                <Save className="h-3 w-3" />
                Save
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={localIsEditing ? handleCancel : handleEdit}
              className="gap-1 hover:bg-gray-100 transition-colors text-xs px-2 py-1"
            >
              {localIsEditing ? (
                <>
                  <X className="h-3 w-3" />
                  Cancel
                </>
              ) : (
                <>
                  <Edit2 className="h-3 w-3" />
                  Edit
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[
            {
              label: "First Name",
              value: localData.firstName || '',
              key: "firstName",
              required: true,
            },
            {
              label: "Last Name",
              value: localData.lastName || '',
              key: "lastName",
              required: true,
            },
            {
              label: "Middle Name",
              value: localData.personal?.personalInfo?.middleName || '',
              key: "personal.personalInfo.middleName",
            },
            {
              label: "Display Name",
              value: localData.personal?.personalInfo?.displayName || '',
              key: "personal.personalInfo.displayName",
            },
            {
              label: "Employee ID",
              value: localData.employeeId,
              key: "employeeId",
            },
            {
              label: "Joining Date",
              value: localData.joiningDate,
              key: "joiningDate",
              type: "date",
            },
            {
              label: "Gender",
              value: localData.personal?.personalInfo?.gender || '',
              key: "personal.personalInfo.gender",
              type: "select",
              options: ["Male", "Female", "Other"],
            },
            {
              label: "Date of Birth",
              value: localData.personal?.personalInfo?.dateOfBirth || '',
              key: "personal.personalInfo.dateOfBirth",
              type: "date",
            },
            {
              label: "Place of Birth",
              value: localData.personal?.personalInfo?.placeOfBirth || '',
              key: "personal.personalInfo.placeOfBirth",
            },
            {
              label: "Blood Group",
              value: localData.personal?.personalInfo?.bloodGroup || '',
              key: "personal.personalInfo.bloodGroup",
              type: "select",
              options: ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"],
            },
            {
              label: "Marital Status",
              value: localData.personal?.personalInfo?.maritalStatus || '',
              key: "personal.personalInfo.maritalStatus",
              type: "select",
              options: ["Unmarried", "Married", "Divorced", "Widowed"],
            },
            {
              label: "Nationality",
              value: localData.personal?.personalInfo?.nationality || '',
              key: "personal.personalInfo.nationality",
            },
            {
              label: "Company Name",
              value: localData.personal?.personalInfo?.companyName || '',
              key: "personal.personalInfo.companyName",
              required: true,
            },
          ].map((field) => (
            <div
              key={field.key}
              className="bg-white rounded-lg border border-gray-200 p-2 hover:shadow-md transition-shadow"
            >
              {localIsEditing ? (
                field.type === "select" ? (
                  <FloatingLabelSelect
                    label={field.label}
                    value={field.value || ""}
                    onChange={(value) =>
                      setLocalData(updateNestedValue(localData, field.key, value))
                    }
                    options={field.options || []}
                    required={field.required}
                  />
                ) : (
                  <FloatingLabelInput
                    label={field.label}
                    value={field.type === "date" ? formatDateForInput(field.value) : (field.value || "")}
                    onChange={(value) =>
                      setLocalData(updateNestedValue(localData, field.key, value))
                    }
                    type={field.type || "text"}
                    required={field.required}
                  />
                )
              ) : (
                <div className="relative">
                  <div className="text-sm text-gray-900 bg-gray-50 px-3 py-3 rounded-md border border-gray-200 min-h-[40px] flex items-center">
                    {field.type === "date" ? formatDateForDisplay(field.value) : (field.value || "Not specified")}
                  </div>
                  <Label className="absolute left-3 text-xs text-gray-500 pointer-events-none -top-2 bg-white px-1">
                    {field.label}
                    {field.required && (
                      <span className="text-red-500 ml-1">*</span>
                    )}
                  </Label>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Education Section
export const EducationSection = ({
  profileData,
  setProfileData,
}: SectionProps) => {
  const [localIsEditing, setLocalIsEditing] = React.useState(false);
  const [localData, setLocalData] = React.useState(profileData);

  React.useEffect(() => {
    setLocalData(profileData);
  }, [profileData]);

  const handleSave = () => {
    // Validate all education entries have required fields
    const educationEntries = localData.personal?.educationEntries || [];
    
    for (let i = 0; i < educationEntries.length; i++) {
      const edu = educationEntries[i];
      if (!edu.degree || !edu.collegeName || !edu.specialization || !edu.startDate || !edu.endDate) {
        toast.error(`Please fill all required fields for Education ${i + 1}`);
        return;
      }
    }
    
    setProfileData(educationEntries as any);
    setLocalIsEditing(false);
    toast.success("Education updated successfully");
  };

  const handleCancel = () => {
    setLocalData(profileData);
    setLocalIsEditing(false);
    toast.info("Edit cancelled");
  };

  const handleEdit = () => {
    setLocalIsEditing(true);
    toast.info("Edit mode enabled");
  };

  const addEducation = () => {
    const currentEntries = localData.personal?.educationEntries || [];
    setLocalData(updateNestedValue(localData, 'personal.educationEntries', [
      ...currentEntries,
      {
        degree: "",
        collegeName: "",
        specialization: "",
        startDate: new Date(),
        endDate: new Date(),
      },
    ]));
  };

  const removeEducation = (index: number) => {
    const currentEntries = localData.personal?.educationEntries || [];
    const newEducationEntries = currentEntries.filter((_, i) => i !== index);
    setLocalData(updateNestedValue(localData, 'personal.educationEntries', newEducationEntries));
  };

  const updateEducation = (index: number, field: string, value: string | Date) => {
    const currentEntries = localData.personal?.educationEntries || [];
    const newEducationEntries = [...currentEntries];
    newEducationEntries[index] = {
      ...newEducationEntries[index],
      [field]: value,
    };
    setLocalData(updateNestedValue(localData, 'personal.educationEntries', newEducationEntries));
  };

  return (
    <Card className="border border-gray-200 shadow-sm bg-white rounded-lg">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <BookOpen className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-base font-semibold text-gray-900">
                Education
              </CardTitle>
              </div>
          </div>
          <div className="flex items-center gap-1">
            {localIsEditing && (
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                className="gap-1 bg-blue-600 hover:bg-blue-700 text-xs px-3 py-1"
              >
                <Save className="h-3 w-3" />
                Save
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={localIsEditing ? handleCancel : handleEdit}
              className="gap-1 hover:bg-gray-50 text-xs px-3 py-1"
            >
              {localIsEditing ? (
                <>
                  <X className="h-3 w-3" />
                  Cancel
                </>
              ) : (
                <>
                  <Edit2 className="h-3 w-3" />
                  Edit
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {(localData.personal?.educationEntries || []).length === 0 && !localIsEditing ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center mx-auto mb-3">
              <BookOpen className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="text-sm font-medium text-gray-800 mb-1">No Education Records</h3>
            <p className="text-xs text-gray-500 mb-4">Add your educational background</p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleEdit}
              className="gap-1 border-dashed border-2 border-gray-300 hover:border-blue-400 hover:bg-blue-50 text-xs"
            >
              <Plus className="h-3 w-3" />
              Add Education
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {(localData.personal?.educationEntries || []).map((edu, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
              >
                {localIsEditing ? (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-blue-50 rounded-lg flex items-center justify-center">
                          <BookOpen className="h-3 w-3 text-blue-600" />
                        </div>
                        <h5 className="font-medium text-sm text-gray-700">
                          Education {index + 1}
                        </h5>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeEducation(index)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs px-2 py-1"
                      >
                        <X className="h-3 w-3 mr-1" />
                        Remove
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-700">
                          Degree <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={edu.degree}
                          onChange={(e) =>
                            updateEducation(index, "degree", e.target.value)
                          }
                          placeholder="Enter degree"
                          className="h-8 text-xs"
                          required
                        />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-700">
                          Institution <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={edu.collegeName}
                          onChange={(e) =>
                            updateEducation(
                              index,
                              "collegeName",
                              e.target.value
                            )
                          }
                          placeholder="Enter institution"
                          className="h-8 text-xs"
                          required
                        />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-700">
                          Specialization <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={edu.specialization}
                          onChange={(e) =>
                            updateEducation(
                              index,
                              "specialization",
                              e.target.value
                            )
                          }
                          placeholder="Enter specialization"
                          className="h-8 text-xs"
                          required
                        />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-700">
                          Start Date <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          type="date"
                          value={edu.startDate instanceof Date ? edu.startDate.toISOString().split('T')[0] : formatDateForInput(edu.startDate)}
                          onChange={(e) =>
                            updateEducation(index, "startDate", new Date(e.target.value))
                          }
                          className="h-8 text-xs"
                          required
                        />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-700">
                          End Date <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          type="date"
                          value={edu.endDate instanceof Date ? edu.endDate.toISOString().split('T')[0] : formatDateForInput(edu.endDate)}
                          onChange={(e) =>
                            updateEducation(index, "endDate", new Date(e.target.value))
                          }
                          className="h-8 text-xs"
                          required
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                          <BookOpen className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-medium text-sm text-gray-900">
                            {edu.degree}
                          </div>
                          <div className="text-gray-600 text-xs">
                            {edu.collegeName}
                          </div>
                        </div>
                      </div>
                      <Badge
                        variant="secondary"
                        className="bg-blue-50 text-blue-600 text-xs font-medium px-3 py-1 rounded-full border border-blue-200"
                      >
                        {edu.startDate ? (edu.startDate instanceof Date ? edu.startDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : new Date(edu.startDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })) : 'Not specified'} - {edu.endDate ? (edu.endDate instanceof Date ? edu.endDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : new Date(edu.endDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })) : 'Present'}
                      </Badge>
                    </div>

                    {edu.specialization && (
                      <div className="flex items-center gap-1 pt-1 border-t border-gray-200">
                        <div className="w-4 h-4 bg-blue-50 rounded flex items-center justify-center">
                          <Code2 className="h-2 w-2 text-blue-600" />
                        </div>
                        <span className="text-xs text-gray-600">
                          {edu.specialization}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {localIsEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={addEducation}
                className="gap-1 w-full text-xs border-dashed"
              >
                <Plus className="h-3 w-3" />
                Add Education
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Family Section
export const FamilySection = ({
  profileData,
  setProfileData,
}: SectionProps) => {
  const [localIsEditing, setLocalIsEditing] = React.useState(false);
  const [localData, setLocalData] = React.useState(profileData);

  React.useEffect(() => {
    setLocalData(profileData);
  }, [profileData]);

  const handleSave = () => {
    const familyMembers = localData.personal?.familyInfo?.familyMembers || [];
    
    // Validate required fields
    const validationErrors: string[] = [];
    
    familyMembers.forEach((member, index) => {
      if (!member.name.trim()) {
        validationErrors.push(`Family Member ${index + 1}: Name is required`);
      }
      if (!member.relationship.trim()) {
        validationErrors.push(`Family Member ${index + 1}: Relationship is required`);
      }
      if (!member.mobile.trim()) {
        validationErrors.push(`Family Member ${index + 1}: Mobile number is required`);
      } else if (!validateMobileNumber(member.mobile)) {
        validationErrors.push(`Family Member ${index + 1}: Mobile number must be exactly 10 digits`);
      }
      if (!member.aadhaar.trim()) {
        validationErrors.push(`Family Member ${index + 1}: Aadhaar number is required`);
      } else if (!validateAadhaarNumber(member.aadhaar)) {
        validationErrors.push(`Family Member ${index + 1}: Aadhaar number must be exactly 12 digits`);
      }
    });
    
    if (validationErrors.length > 0) {
      validationErrors.forEach(error => toast.error(error));
      return;
    }
    
    // Send only the familyInfo object as expected by the parent component
    const familyInfo = {
      familyMembers: familyMembers
    };
    
    setProfileData(familyInfo as any);
    setLocalIsEditing(false);
    toast.success("Family information updated successfully");
  };

  const handleCancel = () => {
    setLocalData(profileData);
    setLocalIsEditing(false);
    toast.info("Edit cancelled");
  };

  const handleEdit = () => {
    setLocalIsEditing(true);
    toast.info("Edit mode enabled");
  };

  const addFamilyMember = () => {
    const currentMembers = localData.personal?.familyInfo?.familyMembers || [];
    setLocalData(updateNestedValue(localData, 'personal.familyInfo.familyMembers', [
      ...currentMembers,
      {
        name: "",
        dateOfBirth: "",
        occupation: "",
        mobile: "",
        aadhaar: "",
        relationship: "",
      },
    ]));
    toast.info("New family member added. Please fill in the required fields.");
  };

  const removeFamilyMember = (index: number) => {
    const currentMembers = localData.personal?.familyInfo?.familyMembers || [];
    const newFamilyMembers = currentMembers.filter((_, i) => i !== index);
    setLocalData(updateNestedValue(localData, 'personal.familyInfo.familyMembers', newFamilyMembers));
  };

  const updateFamilyMember = (index: number, field: string, value: string) => {
    const currentMembers = localData.personal?.familyInfo?.familyMembers || [];
    const newFamilyMembers = [...currentMembers];
    newFamilyMembers[index] = {
      ...newFamilyMembers[index],
      [field]: value,
    };
    setLocalData(updateNestedValue(localData, 'personal.familyInfo.familyMembers', newFamilyMembers));
  };

  return (
    <Card className="border border-gray-200 shadow-sm bg-white rounded-lg">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <Users className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-base font-semibold text-gray-900">
                Family Information
              </CardTitle>
              </div>
          </div>
          <div className="flex items-center gap-1">
            {localIsEditing && (
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                className="gap-1 bg-blue-600 hover:bg-blue-700 text-xs px-3 py-1"
              >
                <Save className="h-3 w-3" />
                Save
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={localIsEditing ? handleCancel : handleEdit}
              className="gap-1 hover:bg-gray-50 text-xs px-3 py-1"
            >
              {localIsEditing ? (
                <>
                  <X className="h-3 w-3" />
                  Cancel
                </>
              ) : (
                <>
                  <Edit2 className="h-3 w-3" />
                  Edit
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {(localData.personal?.familyInfo?.familyMembers || []).length === 0 && !localIsEditing ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Users className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="text-sm font-medium text-gray-800 mb-1">No Family Members</h3>
            <p className="text-xs text-gray-500 mb-4">Add your family members and emergency contacts</p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleEdit}
              className="gap-1 border-dashed border-2 border-gray-300 hover:border-blue-400 hover:bg-blue-50 text-xs"
            >
              <Plus className="h-3 w-3" />
              Add Family Member
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {(localData.personal?.familyInfo?.familyMembers || []).map((member, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
              >
                {localIsEditing ? (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 bg-blue-50 rounded-lg flex items-center justify-center">
                          <Users className="h-3 w-3 text-blue-600" />
                        </div>
                        <h5 className="font-medium text-sm text-gray-700">
                          Family Member {index + 1}
                        </h5>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeFamilyMember(index)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs px-2 py-1"
                      >
                        <X className="h-3 w-3 mr-1" />
                        Remove
                      </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-700">
                          Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={member.name}
                          onChange={(e) =>
                            updateFamilyMember(index, "name", e.target.value)
                          }
                          placeholder="Enter name"
                          className="h-8 text-xs"
                        />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-700">
                          Relationship <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={member.relationship}
                          onChange={(e) =>
                            updateFamilyMember(index, "relationship", e.target.value)
                          }
                          placeholder="Enter relationship"
                          className="h-8 text-xs"
                        />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-700">
                          Date of Birth
                        </Label>
                        <Input
                          type="date"
                          value={formatDateForInput(member.dateOfBirth)}
                          onChange={(e) =>
                            updateFamilyMember(
                              index,
                              "dateOfBirth",
                              e.target.value
                            )
                          }
                          className="h-8 text-xs"
                        />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-700">
                          Occupation
                        </Label>
                        <Input
                          value={member.occupation}
                          onChange={(e) =>
                            updateFamilyMember(
                              index,
                              "occupation",
                              e.target.value
                            )
                          }
                          placeholder="Enter occupation"
                          className="h-8 text-xs"
                        />
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs font-medium text-gray-700">
                          Mobile <span className="text-red-500">*</span>
                        </Label>
                        <div className="flex">
                          <div className="flex items-center px-3 bg-gray-50 border border-r-0 border-gray-300 rounded-l-md text-xs text-gray-600">
                            +91
                          </div>
                          <Input
                            value={formatMobileNumber(member.mobile)}
                            onChange={(e) => {
                              const formatted = formatMobileNumber(e.target.value);
                              updateFamilyMember(index, "mobile", formatted);
                            }}
                            placeholder="Enter 10-digit mobile number"
                            className="h-8 text-xs rounded-l-none"
                            maxLength={10}
                          />
                        </div>
                        {member.mobile && !validateMobileNumber(member.mobile) && (
                          <p className="text-xs text-red-500">
                            Mobile number must be exactly 10 digits
                          </p>
                        )}
                      </div>

                      <div className="space-y-1 md:col-span-2">
                        <Label className="text-xs font-medium text-gray-700">
                          Aadhaar <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={member.aadhaar}
                          onChange={(e) => {
                            // Only allow digits and limit to 12 characters
                            const value = e.target.value.replace(/\D/g, '').slice(0, 12);
                            updateFamilyMember(index, "aadhaar", value);
                          }}
                          placeholder="Enter 12-digit Aadhaar number"
                          className="h-8 text-xs"
                          maxLength={12}
                        />
                        {member.aadhaar && !validateAadhaarNumber(member.aadhaar) && (
                          <p className="text-xs text-red-500">
                            Aadhaar number must be exactly 12 digits
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                          <Users className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <div className="font-medium text-sm text-gray-900">
                            {member.name}
                          </div>
                        </div>
                      </div>
                      <Badge
                        variant="secondary"
                        className="bg-blue-50 text-blue-600 text-xs font-medium px-3 py-1 rounded-full border border-blue-200"
                      >
                      {member.relationship}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1 border-t border-gray-200">
                      {member.occupation && (
                        <div className="flex items-center gap-1">
                          <div className="w-4 h-4 bg-blue-50 rounded flex items-center justify-center">
                            <Briefcase className="h-2 w-2 text-blue-600" />
                          </div>
                          <span className="text-xs text-gray-600">
                           Occupation: {member.occupation}
                          </span>
                        </div>
                      )}
                      {member.mobile && (
                        <div className="flex items-center gap-1">
                          <div className="w-4 h-4 bg-blue-50 rounded flex items-center justify-center">
                            <Phone className="h-2 w-2 text-blue-600" />
                          </div>
                          <span className="text-xs text-gray-600">
                            {formatMobileWithCountryCode(member.mobile)}
                          </span>
                        </div>
                      )}
                      {member.dateOfBirth && (
                        <div className="flex items-center gap-1">
                          <div className="w-4 h-4 bg-blue-50 rounded flex items-center justify-center">
                            <Calendar className="h-2 w-2 text-blue-600" />
                          </div>
                          <span className="text-xs text-gray-600">
                            DOB: {formatDateForDisplay(member.dateOfBirth)}
                          </span>
                        </div>
                      )}
                      {member.aadhaar && (
                        <div className="flex items-center gap-1">
                          <div className="w-4 h-4 bg-blue-50 rounded flex items-center justify-center">
                            <Shield className="h-2 w-2 text-blue-600" />
                          </div>
                          <span className="text-xs text-gray-600">
                            Aadhaar: {member.aadhaar}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}

          {localIsEditing && (
            <Button
              variant="outline"
              size="sm"
              onClick={addFamilyMember}
              className="gap-1 w-full text-xs border-dashed"
            >
              <Plus className="h-3 w-3" />
              Add Family Member
            </Button>
          )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Address Section
export const AddressSection = ({
  profileData,
  setProfileData,
}: SectionProps) => {
  const [localIsEditing, setLocalIsEditing] = React.useState(false);
  const [localData, setLocalData] = React.useState(profileData);

  React.useEffect(() => {
    setLocalData(profileData);
  }, [profileData]);

  const handleSave = () => {
    // Send only the addressInfo object as expected by the parent component
    const addressInfo = {
      currentAddress1: localData.personal?.addressInfo?.currentAddress1 || '',
      currentAddress2: localData.personal?.addressInfo?.currentAddress2 || '',
      currentCity: localData.personal?.addressInfo?.currentCity || '',
      currentCountry: localData.personal?.addressInfo?.currentCountry || '',
      currentState: localData.personal?.addressInfo?.currentState || '',
      currentPinCode: localData.personal?.addressInfo?.currentPinCode || '',
      sameAsCurrentAddress: localData.personal?.addressInfo?.sameAsCurrentAddress || false,
      permanentAddress1: localData.personal?.addressInfo?.permanentAddress1 || '',
      permanentAddress2: localData.personal?.addressInfo?.permanentAddress2 || '',
      permanentCity: localData.personal?.addressInfo?.permanentCity || '',
      permanentCountry: localData.personal?.addressInfo?.permanentCountry || '',
      permanentState: localData.personal?.addressInfo?.permanentState || '',
      permanentPinCode: localData.personal?.addressInfo?.permanentPinCode || ''
    };
    
    setProfileData(addressInfo as any);
    setLocalIsEditing(false);
    toast.success("Address information updated successfully");
  };

  const handleCancel = () => {
    setLocalData(profileData);
    setLocalIsEditing(false);
    toast.info("Edit cancelled");
  };

  const handleEdit = () => {
    setLocalIsEditing(true);
    toast.info("Edit mode enabled");
  };

  return (
    <Card className="border-none shadow-sm bg-white">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base font-bold bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
              Address Information
            </CardTitle>
          </div>
          <div className="flex items-center gap-1">
            {localIsEditing && (
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                className="gap-1 bg-blue-600 hover:bg-blue-700 text-xs px-2 py-1"
              >
                <Save className="h-3 w-3" />
                Save
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={localIsEditing ? handleCancel : handleEdit}
              className="gap-1 hover:bg-gray-100 transition-colors text-xs px-2 py-1"
            >
              {localIsEditing ? (
                <>
                  <X className="h-3 w-3" />
                  Cancel
                </>
              ) : (
                <>
                  <Edit2 className="h-3 w-3" />
                  Edit
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {/* Current Address */}
        <div>
          <h3 className="font-medium mb-2 text-gray-800 flex items-center gap-1 text-sm">
            <MapPin className="h-4 w-4 text-blue-500" />
            Current Address
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[
              {
                label: "Address Line 1",
                value: localData.personal?.addressInfo?.currentAddress1 || '',
                key: "personal.addressInfo.currentAddress1",
              },
              {
                label: "Address Line 2",
                value: localData.personal?.addressInfo?.currentAddress2 || '',
                key: "personal.addressInfo.currentAddress2",
              },
              {
                label: "City",
                value: localData.personal?.addressInfo?.currentCity || '',
                key: "personal.addressInfo.currentCity",
              },
              {
                label: "State",
                value: localData.personal?.addressInfo?.currentState || '',
                key: "personal.addressInfo.currentState",
              },
              {
                label: "Country",
                value: localData.personal?.addressInfo?.currentCountry || '',
                key: "personal.addressInfo.currentCountry",
              },
              {
                label: "Pin Code",
                value: localData.personal?.addressInfo?.currentPinCode || '',
                key: "personal.addressInfo.currentPinCode",
              },
            ].map((field) => (
              <div
                key={field.key}
                className="bg-white rounded-lg border border-gray-200 p-2 hover:shadow-md transition-shadow"
              >
                {localIsEditing ? (
                  <FloatingLabelInput
                    label={field.label}
                    value={field.value || ""}
                    onChange={(value) =>
                      setLocalData(updateNestedValue(localData, field.key, value))
                    }
                  />
                ) : (
                  <div className="relative">
                    <div className="text-sm text-gray-900 bg-gray-50 px-3 py-3 rounded-md border border-gray-200 min-h-[40px] flex items-center">
                      {field.value || "Not provided"}
                    </div>
                    <Label className="absolute left-3 text-xs text-gray-500 pointer-events-none -top-2 bg-white px-1">
                      {field.label}
                    </Label>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <Separator />

        {/* Same as Current Address Toggle */}
        <div className="flex items-center gap-2 p-2 bg-blue-50/50 rounded-lg border border-blue-100">
          <Switch
            checked={localData.personal?.addressInfo?.sameAsCurrentAddress || false}
            onCheckedChange={(checked) =>
              setLocalData(updateNestedValue(localData, 'personal.addressInfo.sameAsCurrentAddress', checked))
            }
            disabled={!localIsEditing}
          />
          <Label className="text-xs font-medium text-gray-700">
            Permanent address is same as current address
          </Label>
        </div>

        {/* Permanent Address */}
        <div>
          <h3 className="font-medium mb-2 text-gray-800 flex items-center gap-1 text-sm">
            <MapPin className="h-4 w-4 text-blue-500" />
            Permanent Address
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[
              {
                label: "Address Line 1",
                value: localData.personal?.addressInfo?.permanentAddress1 || '',
                key: "personal.addressInfo.permanentAddress1",
              },
              {
                label: "Address Line 2",
                value: localData.personal?.addressInfo?.permanentAddress2 || '',
                key: "personal.addressInfo.permanentAddress2",
              },
              {
                label: "City",
                value: localData.personal?.addressInfo?.permanentCity || '',
                key: "personal.addressInfo.permanentCity",
              },
              {
                label: "State",
                value: localData.personal?.addressInfo?.permanentState || '',
                key: "personal.addressInfo.permanentState",
              },
              {
                label: "Country",
                value: localData.personal?.addressInfo?.permanentCountry || '',
                key: "personal.addressInfo.permanentCountry",
              },
              {
                label: "Pin Code",
                value: localData.personal?.addressInfo?.permanentPinCode || '',
                key: "personal.addressInfo.permanentPinCode",
              },
            ].map((field) => (
              <div
                key={field.key}
                className="bg-white rounded-lg border border-gray-200 p-2 hover:shadow-md transition-shadow"
              >
                {localIsEditing ? (
                  <FloatingLabelInput
                    label={field.label}
                    value={
                      localData.personal?.addressInfo?.sameAsCurrentAddress
                        ? String(
                            localData.personal?.addressInfo?.[
                              field.key.replace(
                                "personal.addressInfo.permanent",
                                "current"
                              ) as keyof typeof localData.personal.addressInfo
                            ] || ""
                          )
                        : String(field.value || "")
                    }
                    onChange={(value) =>
                      setLocalData(updateNestedValue(localData, field.key, value))
                    }
                    disabled={localData.personal?.addressInfo?.sameAsCurrentAddress}
                  />
                ) : (
                  <div className="relative">
                    <div className="text-sm text-gray-900 bg-gray-50 px-3 py-3 rounded-md border border-gray-200 min-h-[40px] flex items-center">
                      {localData.personal?.addressInfo?.sameAsCurrentAddress
                        ? String(
                            localData.personal?.addressInfo?.[
                              field.key.replace(
                                "personal.addressInfo.permanent",
                                "current"
                              ) as keyof typeof localData.personal.addressInfo
                            ] || "Not provided"
                          )
                        : String(field.value || "Not provided")}
                    </div>
                    <Label className="absolute left-3 text-xs text-gray-500 pointer-events-none -top-2 bg-white px-1">
                      {field.label}
                    </Label>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Previous Employer Section
export const PreviousEmployerSection = ({
  experienceType,
  setExperienceType,
  experienceDetails,
  setExperienceDetails,
  setProfileData,
}: PreviousEmployerProps) => {
  const [localIsEditing, setLocalIsEditing] = React.useState(false);
  const [localExperienceType, setLocalExperienceType] =
    React.useState(experienceType);
  const [localExperienceDetails, setLocalExperienceDetails] =
    React.useState({
      ...experienceDetails,
      experienceType: experienceType,
      totalYears: experienceDetails.totalYears || 0,
      totalMonths: experienceDetails.totalMonths || 0,
      previousEmployers: experienceDetails.previousEmployers || []
    });

  // Check if we should show fresher state
  const shouldShowFresher = React.useMemo(() => {
    return (
      localExperienceType === "fresher" || 
      experienceDetails.experienceType === "fresher" || 
      localExperienceDetails.experienceType === "fresher" ||
      (localExperienceDetails.previousEmployers.length === 0 && 
       localExperienceDetails.totalYears === 0 && 
       localExperienceDetails.totalMonths === 0)
    );
  }, [localExperienceType, experienceDetails.experienceType, localExperienceDetails]);

  React.useEffect(() => {
    setLocalExperienceType(experienceType);
    setLocalExperienceDetails({
      ...experienceDetails,
      experienceType: experienceType,
      totalYears: experienceDetails.totalYears || 0,
      totalMonths: experienceDetails.totalMonths || 0,
      previousEmployers: experienceDetails.previousEmployers || []
    });
  }, [experienceType, experienceDetails]);


  const handleSave = () => {
    setExperienceType(localExperienceType);
    
    // Update experienceDetails with experienceType
    const updatedExperienceDetails = {
      ...localExperienceDetails,
      experienceType: localExperienceType
    };
    
    setExperienceDetails(updatedExperienceDetails);
    
    // Send only the experienceDetails object as expected by the parent component
    setProfileData(updatedExperienceDetails as any);
    
    setLocalIsEditing(false);
    toast.success("Previous employer details updated successfully");
  };

  const handleCancel = () => {
    setLocalExperienceType(experienceType);
    setLocalExperienceDetails({
      ...experienceDetails,
      experienceType: experienceType,
      totalYears: experienceDetails.totalYears || 0,
      totalMonths: experienceDetails.totalMonths || 0,
      previousEmployers: experienceDetails.previousEmployers || []
    });
    setLocalIsEditing(false);
    toast.info("Edit cancelled");
  };

  const handleEdit = () => {
    setLocalIsEditing(true);
    toast.info("Edit mode enabled");
  };

  return (
    <Card className="border border-gray-200 shadow-sm bg-white rounded-lg">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <Briefcase className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-base font-semibold text-gray-900">
                Previous Employee Details
              </CardTitle>
              </div>
          </div>
          <div className="flex items-center gap-1">
            {localIsEditing && (
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                className="gap-1 bg-blue-600 hover:bg-blue-700 text-xs px-3 py-1"
              >
                <Save className="h-3 w-3" />
                Save
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={localIsEditing ? handleCancel : handleEdit}
              className="gap-1 hover:bg-gray-50 text-xs px-3 py-1"
            >
              {localIsEditing ? (
                <>
                  <X className="h-3 w-3" />
                  Cancel
                </>
              ) : (
                <>
                  <Edit2 className="h-3 w-3" />
                  Edit
                </>
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        
        {shouldShowFresher && !localIsEditing ? (
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div className="w-2 h-8 bg-blue-500 rounded-full"></div>
              <div>
                <h3 className="text-sm font-medium text-gray-900">Work Experience</h3>
                </div>
            </div>
            <Badge 
              variant="secondary" 
              className="bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1 text-sm font-medium"
            >
              Fresher
            </Badge>
          </div>
        ) : localExperienceType === "experience" && localExperienceDetails.previousEmployers.length === 0 && !localIsEditing ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center mx-auto mb-3">
              <Briefcase className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="text-sm font-medium text-gray-800 mb-1">No Previous Employers</h3>
            <p className="text-xs text-gray-500 mb-4">Add previous work experience details</p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleEdit}
              className="gap-1 border-dashed border-2 border-gray-300 hover:border-blue-400 hover:bg-blue-50 text-xs"
            >
              <Plus className="h-3 w-3" />
              Add Experience
            </Button>
          </div>
        ) : localIsEditing ? (
          <>
            <div className="flex items-center gap-2">
              <Select
                value={localExperienceType}
                onValueChange={(value: "fresher" | "experience") => {
                  setLocalExperienceType(value);
                  // Clear previous employer data when switching to fresher
                  if (value === "fresher") {
                    setLocalExperienceDetails({
                      experienceType: value,
                      totalYears: 0,
                      totalMonths: 0,
                      previousEmployers: []
                    });
                  }
                }}
                disabled={!localIsEditing}
              >
                <SelectTrigger className="bg-white border-gray-200 focus:border-blue-300 h-8 text-xs">
                  <SelectValue placeholder="Select experience type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fresher">Fresher</SelectItem>
                  <SelectItem value="experience">Experience</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {localExperienceType === "fresher" && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                    <Briefcase className="h-3 w-3 text-blue-600" />
                  </div>
                  <div className="text-sm text-blue-800">
                    <strong>Fresher:</strong> No previous employer details required
                  </div>
                </div>
              </div>
            )}

            {localExperienceType === "experience" && (
              <div className="space-y-2">
                <div>
                  <Label className="text-xs font-medium text-gray-700">
                    Total Work Experience
                  </Label>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs font-medium text-gray-700">
                        Years <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        value={localExperienceDetails.totalYears}
                        onChange={(e) =>
                          setLocalExperienceDetails({
                            ...localExperienceDetails,
                            experienceType: localExperienceType,
                            totalYears: parseInt(e.target.value) || 0,
                          })
                        }
                        placeholder="Years"
                        className="bg-white border-gray-200 focus:border-blue-300 h-8 text-xs"
                        disabled={!localIsEditing}
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-medium text-gray-700">
                        Months <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        max="11"
                        value={localExperienceDetails.totalMonths}
                        onChange={(e) =>
                          setLocalExperienceDetails({
                            ...localExperienceDetails,
                            experienceType: localExperienceType,
                            totalMonths: parseInt(e.target.value) || 0,
                          })
                        }
                        placeholder="Months"
                        className="bg-white border-gray-200 focus:border-blue-300 h-8 text-xs"
                        disabled={!localIsEditing}
                        required
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-medium text-gray-700">
                    Previous Employers
                  </Label>
                  {localExperienceDetails.previousEmployers.map(
                    (emp, index) => (
                        <div
                          key={index}
                          className="bg-gray-50 rounded-lg border border-gray-200 p-4 mt-2 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 bg-blue-50 rounded-lg flex items-center justify-center">
                                <Building2 className="h-3 w-3 text-blue-600" />
                              </div>
                              <h4 className="font-medium text-sm text-gray-700">
                                Previous Employer {index + 1}
                              </h4>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const newEmployers =
                                  localExperienceDetails.previousEmployers.filter(
                                    (_, i) => i !== index
                                  );
                                setLocalExperienceDetails({
                                  ...localExperienceDetails,
                                  experienceType: localExperienceType,
                                  previousEmployers: newEmployers,
                                });
                              }}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 text-xs px-2 py-1"
                            >
                              <X className="h-3 w-3 mr-1" />
                              Remove
                            </Button>
                          </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <Label className="text-xs font-medium text-gray-600">
                              Company Name <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              value={emp.companyName}
                              onChange={(e) => {
                                const newEmployers = [
                                  ...localExperienceDetails.previousEmployers,
                                ];
                                newEmployers[index].companyName =
                                  e.target.value;
                                setLocalExperienceDetails({
                                  ...localExperienceDetails,
                                  experienceType: localExperienceType,
                                  previousEmployers: newEmployers,
                                });
                              }}
                              placeholder="Enter company name"
                              className="bg-white border-gray-200 focus:border-blue-300 h-8 text-xs"
                              required
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs font-medium text-gray-600">
                              Department
                            </Label>
                            <Input
                              value={emp.department}
                              onChange={(e) => {
                                const newEmployers = [
                                  ...localExperienceDetails.previousEmployers,
                                ];
                                newEmployers[index].department = e.target.value;
                                setLocalExperienceDetails({
                                  ...localExperienceDetails,
                                  experienceType: localExperienceType,
                                  previousEmployers: newEmployers,
                                });
                              }}
                              placeholder="Enter department"
                              className="bg-white border-gray-200 focus:border-blue-300 h-8 text-xs"
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <div className="space-y-1">
                            <Label className="text-xs font-medium text-gray-600">
                              Job Title <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              value={emp.jobTitle}
                              onChange={(e) => {
                                const newEmployers = [
                                  ...localExperienceDetails.previousEmployers,
                                ];
                                newEmployers[index].jobTitle = e.target.value;
                                setLocalExperienceDetails({
                                  ...localExperienceDetails,
                                  experienceType: localExperienceType,
                                  previousEmployers: newEmployers,
                                });
                              }}
                              placeholder="Enter job title"
                              className="bg-white border-gray-200 focus:border-blue-300 h-8 text-xs"
                              required
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs font-medium text-gray-600">
                              Start Date <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              type="date"
                              value={formatDateForInputField(emp.startDate)}
                              onChange={(e) => {
                                const newEmployers = [
                                  ...localExperienceDetails.previousEmployers,
                                ];
                                newEmployers[index].startDate = e.target.value ? new Date(e.target.value) : new Date();
                                setLocalExperienceDetails({
                                  ...localExperienceDetails,
                                  experienceType: localExperienceType,
                                  previousEmployers: newEmployers,
                                });
                              }}
                              className="bg-white border-gray-200 focus:border-blue-300 h-8 text-xs"
                              required
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs font-medium text-gray-600">
                            End Date <span className="text-red-500">*</span>
                          </Label>
                          <Input
                            type="date"
                            value={formatDateForInputField(emp.endDate)}
                            onChange={(e) => {
                              const newEmployers = [
                                ...localExperienceDetails.previousEmployers,
                              ];
                              newEmployers[index].endDate = e.target.value ? new Date(e.target.value) : new Date();
                              setLocalExperienceDetails({
                                ...localExperienceDetails,
                                experienceType: localExperienceType,
                                previousEmployers: newEmployers,
                              });
                            }}
                            className="bg-white border-gray-200 focus:border-blue-300 h-8 text-xs"
                            required
                          />
                        </div>
                      </div>
                    )
                  )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setLocalExperienceDetails({
                          ...localExperienceDetails,
                          experienceType: localExperienceType,
                          previousEmployers: [
                            ...localExperienceDetails.previousEmployers,
                            {
                              companyName: "",
                              department: "",
                              jobTitle: "",
                              startDate: new Date(),
                              endDate: new Date(),
                            },
                          ],
                        });
                      }}
                      className="gap-1 w-full mt-2 text-xs border-dashed"
                    >
                      <Plus className="h-3 w-3" />
                      Add Previous Employer
                    </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="font-medium text-sm text-gray-900">
                  {localExperienceType === "fresher"
                    ? "Fresher"
                    : "Experience"}
                </div>
              </div>
              <Badge
                variant="secondary"
                className="bg-blue-50 text-blue-600 text-xs font-medium px-3 py-1 rounded-full border border-blue-200"
              >
                {localExperienceType === "experience"
                  ? `${localExperienceDetails.totalYears} years ${localExperienceDetails.totalMonths} months`
                  : "No Experience"}
              </Badge>
            </div>

            {localExperienceType === "experience" &&
              localExperienceDetails.previousEmployers.length > 0 && (
                <div className="space-y-3">
                  {localExperienceDetails.previousEmployers.map(
                    (emp, index) => (
                      <div
                        key={index}
                        className="bg-gray-50 rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 bg-blue-50 rounded-lg flex items-center justify-center">
                                <Building2 className="h-3 w-3 text-blue-600" />
                              </div>
                              <div>
                                <div className="font-medium text-sm text-gray-900">
                                  {emp.companyName}
                                </div>
                                <div className="text-gray-600 text-xs">
                                  {emp.jobTitle}
                                </div>
                              </div>
                            </div>
                            <Badge
                              variant="secondary"
                              className="bg-blue-50 text-blue-600 text-xs font-medium px-3 py-1 rounded-full border border-blue-200"
                            >
                              {emp.startDate ? (emp.startDate instanceof Date ? emp.startDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : new Date(emp.startDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })) : 'Not specified'} - {emp.endDate ? (emp.endDate instanceof Date ? emp.endDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : new Date(emp.endDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })) : 'Present'}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-blue-50 rounded-lg flex items-center justify-center">
                              <Briefcase className="h-3 w-3 text-blue-600" />
                            </div>
                            <div className="text-sm text-gray-900">
                              {emp.department || 'Department not specified'}
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </div>
              )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
